import React, { useEffect, useRef } from "react";
// Wallet Balance

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import RefreshIcon from "@mui/icons-material/Refresh";

// Custom
import CustomInput from "../../forms/input/Input";
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import CustomToast from "../toast/CustomToast";
import BackdropSpinner from "../spinner/BackdropSpinner";
import BillingDetailsModal from "../modals/BillingDetailsModal";
import CustomSearch from "../../components/search/Search";

// React icons
import { FaArrowUp } from "react-icons/fa";
import { FaArrowDown } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa";

// Svg
import Calendar2 from "../../assets/svg/calendar2";

// Redux
import {
  setBalance,
  setTransactionsHistory,
  setWorkOrderHistory,
  setBillingDeterminants,
  setWorkOrderId,
  setBillingData,
  setSelectedBillingData,
  setSelectedBillingDeterminant,
  setSelectedMeter,
  setWalletBalance,
} from "../../redux/features/BillingDeterminantSlice";

// Utils
import { fetchGETRequest, fetchPUTRequest } from "../../utils/services";
import {
  getTimeStamp,
  formatTime,
  formatDate,
  isUUID,
} from "../../utils/helper";
import useEmailJS from "../../hooks/useEmailJS";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  BillingDeterminationItem,
  ToastState,
  TransactionItem,
} from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

export const ConsumerBillingDeterminants = ({
  meterId: propMeterId,
}: {
  meterId?: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [tariffValue, setTariffValue] = useState("");
  const [currentTariff, setCurrentTariff] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [loader, setLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const dispatch = useAppDispatch();
  const requestMeterId = useRef<string | null>(null);

  const {
    balance,
    transactionsHistory,
    workOrderHistory,
    billingDeterminants,
    billingData,
    selectedBillingDeterminant,
    selectedBillingData,
    selectedMeter,
    walletBalance,
    workOrderId,
  } = useAppSelector((state) => state.billing);

  const handleBillingDeterminant = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }
    // const deviceId = "30d33fa0-bb60-11ef-85d5-e5b1b2e5df91"
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));
    const keys = "IP_KWH,EventCode";
    const intervalType = "WEEK";

    const response: any = await fetchGETRequest(
      `plugins/telemetry/DEVICE/${selectedMeter}/getBillingDeterminants?keys=${keys}&startTs=${startTs}&endTs=${endTs}&intervalType=${intervalType}`
    );

    if (response?.IP_KWH && response?.IP_KWH?.length > 0) {
      const values = response?.IP_KWH?.map((item: { value: string }) =>
        parseFloat(item.value)
      );
      const max = Math.max(...values);
      const min = Math.min(...values);
      const totalCost = max - min;

      let penaltyCount = 0;

      const penalty = response?.EventCode?.map((item: { value: string }) => {
        if (parseFloat(item.value) >= 200 && parseFloat(item.value) < 300) {
          return penaltyCount++;
        }
      });

      const newBalance = balance - totalCost;

      const newBillingDeterminant: BillingDeterminationItem = {
        id: billingDeterminants?.length + 1,
        fromDate: formatDate(startTs, "YYYY-MM-DDTHH:mm"),
        toDate: formatDate(endTs, "YYYY-MM-DDTHH:mm"),
        timestamp: new Date().toISOString(),
      };

      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: -totalCost,
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction])
      );

      dispatch(
        setBillingDeterminants([...billingDeterminants, newBillingDeterminant])
      );

      const newBillingData = {
        id: newBillingDeterminant.id,
        seqNo: billingDeterminants.length + 1,
        registerType: selectedMeter,
        qu: max.toFixed(2),
        activeEnergy: "1.0.1.8.0.255",
        ut: min.toFixed(2),
        qt: totalCost.toFixed(2),
        penaltyCount,
      };

      dispatch(setBillingData([...billingData, newBillingData]));
      // localStorage.setItem(`billingData_${newBillingDeterminant.id}`, JSON.stringify(newBillingData));
      // dispatch(setBalance(newBalance));
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);

      // if (newBalance < 20) {
      //   // await createWorkOrder('disconnect');
      //   setToast({ message: "Wallet balance is below 20. Disconnect work order created.", severity: "error" });
      // }
    } else {
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);
      setToast({
        message: "No billing data found for the selected time range.",
        severity: "error",
      });
    }

    setAnchorEl(null);
  };

  const fetchCurrentTariff = async (id: string) => {
    if (!id) {
      setCurrentTariff(null);
      return;
    }

    setLoader(true);
    setCurrentTariff(null); // Reset before fetching new data
    requestMeterId.current = id; // Track the current request's target ID

    try {
      const body = {
        command: "GetCurrentMeterTariff",
        deviceIds: [id],
        projectName: "",
      };

      const response: any = await fetchPUTRequest(`devices/command`, body);

      // Check if the component is still interested in this specific meter's data
      if (requestMeterId.current !== id) {
        console.log(`Ignoring stale tariff data for meter: ${id}`);
        return;
      }

      // Being flexible with response fields
      const tariffResult =
        response?.TariffAmount ||
        response?.value ||
        response?.result ||
        response?.data ||
        response?.response ||
        (typeof response === "string" ? response : null);

      if (tariffResult !== undefined && tariffResult !== null) {
        setCurrentTariff(tariffResult.toString());
      } else {
        // If no results specifically found for this meter
        setCurrentTariff(null);
        if (response?.success) {
          setToast({
            message: "Current tariff fetched successfully (none assigned).",
            severity: "info",
          });
        }
      }
    } catch (error) {
      console.error("Error fetching current tariff:", error);
      // Only reset if this was still the target request
      if (requestMeterId.current === id) {
        setCurrentTariff(null);
      }
    } finally {
      // Only disable loader if this was the latest request
      if (requestMeterId.current === id) {
        setLoader(false);
      }
    }
  };

  useEffect(() => {
    const targetMeter = propMeterId || selectedMeter;
    if (targetMeter) {
      fetchCurrentTariff(targetMeter);
    } else {
      setCurrentTariff(null);
    }

    // Cleanup function to avoid setting state on unmounted component
    return () => {
      requestMeterId.current = null;
    };
  }, [propMeterId, selectedMeter]);

  const handleSaveTariff = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please select a meter.",
        severity: "error",
      });
      return;
    }

    if (!tariffValue) {
      setToast({
        message: "Please enter a tariff value.",
        severity: "error",
      });
      return;
    }

    setLoader(true);
    try {
      const body = {
        command: "SetMeterTariff",
        value: tariffValue,
        deviceIds: [selectedMeter],
        projectName: "",
      };

      const response: any = await fetchPUTRequest(`devices/command`, body);

      if (response?.success) {
        setToast({
          message: "Tariff value saved successfully.",
          severity: "success",
        });
        setTariffValue("");
        const targetMeter = propMeterId || selectedMeter;
        if (targetMeter) {
          fetchCurrentTariff(targetMeter);
        }
      } else {
        setToast({
          message: response?.message || "Failed to save tariff value.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error saving tariff:", error);
      setToast({
        message: "An error occurred while saving the tariff value.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const GenerateBillingDeterminantCard = (
    item: BillingDeterminationItem,
    index: number
  ) => {
    return (
      <Box
        className="wallet_balance_billing_determinant_row"
        sx={{
          backgroundColor: "#e3f1fa",
          "&:hover": {
            backgroundColor: "#e0f7fa",
          },
          cursor: "pointer",
        }}
        onClick={() => {
          const savedBillingData = billingData?.filter(
            (bill: any) => bill?.id === item?.id
          );
          dispatch(setSelectedBillingData(savedBillingData));
          dispatch(setSelectedBillingDeterminant(item));
          setOpenModal(true);
        }}
        key={index}
      >
        <Typography variant="body2" fontWeight={"500"}>
          Billing Determinant:{" "}
          {formatDate(new Date(item?.fromDate), "DD/MM/YYYY HH:mm:ss")}{" "}
          <Typography component="span">
            <UnifiedIcon icon={FaArrowRight} color="#181B25" fontSize="10px" />
          </Typography>{" "}
          {formatDate(new Date(item?.toDate), "DD/MM/YYYY HH:mm:ss")}{" "}
        </Typography>
        <Typography variant="body2" fontWeight={400}>
          {formatDate(item?.timestamp, "DD-MM-YYYY HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleTimeRangeChange = (timeRange: number | number[]) => {
    if (Array.isArray(timeRange)) {
      setTimeRange(timeRange);
    } else {
      setTimeRange([timeRange, timeRange]);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <BackdropSpinner open={loader} />
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />
      <Card
        sx={{
          marginTop: "20px",
          borderWidth: "1px",
          borderColor: "#E0E0E0",
        }}
      >
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Tariff Details
              </Typography>
              {currentTariff !== null && (
                <Typography variant="body2" color="textSecondary">
                  Current Tariff: <b>{currentTariff}</b>
                </Typography>
              )}
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 10 }}>
              <CustomInput
                label="Tariff Value"
                placeholder="Enter Tariff Value"
                value={tariffValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTariffValue(e.target.value)
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <ContainedButton
                label="Save"
                onClick={handleSaveTariff}
                loading={loader}
                sx={{ width: "100%", height: "35px", marginTop: "23px" }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card
        sx={{
          marginTop: "20px",
          overflow: "auto",
          height: "270px",
          borderWidth: "1px",
          borderColor: "#E0E0E0",
        }}
      >
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="body1" fontWeight={"600"}>
                Generate Billing Determinant
              </Typography>
              <IconButton
                onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                  setAnchorEl(event.currentTarget)
                }
              >
                <Calendar2 />
              </IconButton>
            </Stack>
          }
        />
        <Divider />

        <CardContent
          sx={{ display: "flex", flexDirection: "column", gap: "15px" }}
        >
          {billingDeterminants?.map(
            (item: BillingDeterminationItem, index: number) => {
              return GenerateBillingDeterminantCard(item, index);
            }
          )}

          {billingDeterminants?.length === 0 && (
            <Stack direction={"row"} justifyContent={"center"}>
              <Typography variant="body1">No Data found</Typography>
            </Stack>
          )}

          <DateAndTimePopover
            anchorEl={anchorEl}
            setAnchorEl={(anchorEl: HTMLElement | null) =>
              setAnchorEl(anchorEl)
            }
            startDate={startDate}
            endDate={endDate}
            timeRange={timeRange}
            setTimeRange={handleTimeRangeChange}
            handleDateChange={handleDateChange}
            handleDateSubmit={handleBillingDeterminant}
            btnText={"Generate Billing Determinant"}
          />
          <BillingDetailsModal
            openModal={openModal}
            setOpenModal={setOpenModal}
            selectedBillingData={selectedBillingData}
            selectedBillingDeterminant={selectedBillingDeterminant}
            setSelectedBillingDeterminant={() => {
              dispatch(setSelectedBillingDeterminant);
            }}
          />
        </CardContent>
      </Card>
    </Box>
  );
};
